import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
}

export function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:type', ogType);
    if (ogImage) {
      setMeta('og:image', ogImage);
      if (ogImageAlt) setMeta('og:image:alt', ogImageAlt);
    }

    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
      setMeta('twitter:card', 'summary_large_image');
    }

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    if (articleAuthor) setMeta('article:author', articleAuthor);
    if (articleSection) setMeta('article:section', articleSection);
    if (articleTags) {
      articleTags.forEach((tag) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
      });
    }
  }, [title, description, keywords, ogType, ogImage, ogImageAlt, articlePublishedTime, articleModifiedTime, articleAuthor, articleSection, articleTags]);

  useEffect(() => {
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }
  }, [canonicalUrl]);

  return null;
}